import React from 'react';
import Button from '@mui/material/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { makeStyles } from '@mui/styles';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { Variant } from '@mui/material/styles/createTypography';
import { ICompanySchemaPartial } from '../../../graphql/type/company';
import useLanguage from '../_hook/useLanguage';
import useKeepitalRoutes from '../_hook/useKeepitalRoutes';
import CopyIconButton from '../_iconButton/CopyIconButton';
import ExternalLinkIconButton from '../_iconButton/ExternalLinkIconButton';
import useRoutes from '../_hook/useRoutes';
import useEnvironment from '../_hook/useEnvironment';
import useAdminRoutes from '../_hook/useAdminRoutes';

interface IProps {
  company: ICompanySchemaPartial;
  useLink?: boolean;
  useAka?: boolean;
  useCopyButton?: boolean;
  showVerification?: boolean;
  showExternalLink?: boolean;
  useIndexLink?: boolean;
  typography?: boolean;
  typographyVariant?: Variant;
  nameTransform?: (name: string) => string;
}

const useStyles = makeStyles({
  iconVerification: {
    fontSize: '16px',
  },
  textOnlyRoot: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  verificationContainer: {
    display: 'inline-flex',
    alignItems: 'center',
  },
});

export default function SchemaCompanyName(props: IProps) {
  const {
    company,
    useLink,
    useAka,
    useCopyButton,
    showVerification,
    showExternalLink,
    useIndexLink,
    typography,
    typographyVariant,
    nameTransform,
  } = props;
  const { aka, name } = company;
  const { t } = useTranslation();
  const classes = useStyles();
  const { getText } = useLanguage();
  const { path } = useRoutes();
  const { path: keepitalPath } = useKeepitalRoutes();
  const { path: adminPath } = useAdminRoutes();

  let text = '';
  if (name) {
    let companyName = getText(name);
    if (nameTransform) {
      companyName = nameTransform(companyName);
    }
    text = companyName;
  }
  if (useAka && aka) text = getText(aka);

  const copyButton = (
    <CopyIconButton iconHtmlFontSize="0.8rem" copyText={text} />
  );

  const verificationComponent =
    showVerification &&
    company.verification !== undefined &&
    company.verification !== null ? (
      <Tooltip title={company.verification ? t('verify') : t('unverify')}>
        <span className={classes.verificationContainer}>
          &nbsp;
          {company.verification ? (
            <CheckCircleIcon
              className={classes.iconVerification}
              htmlColor="#80C53A"
              fontSize="small"
            />
          ) : (
            <CancelIcon
              className={classes.iconVerification}
              htmlColor="#F92949"
              fontSize="small"
            />
          )}
        </span>
      </Tooltip>
    ) : null;

  const externalLinkComponent =
    showExternalLink && company.origin && company.slug ? (
      <ExternalLinkIconButton
        onClick={(e) => e.stopPropagation()}
        tooltip={`${t('company')} ${t('page')}`}
        href={keepitalPath('company.index', {
          origin: company.origin.toLowerCase(),
          companySlug: company.slug,
        })}
        target="_blank"
        iconFontSize="small"
      />
    ) : null;

  if (!typography && (useLink || useIndexLink) && company._id) {
    return (
      <>
        <Button
          disableFocusRipple
          disableElevation
          href={
            useIndexLink && company.origin && company.slug
              ? keepitalPath('company.index', {
                  origin: company.origin.toLowerCase(),
                  companySlug: company.slug,
                })
              : adminPath('business.dashboard', {
                  companyID: company._id as string,
                })
          }
          color="primary"
          target="_blank"
        >
          {text}
        </Button>
        {verificationComponent}
        {useCopyButton && copyButton}
      </>
    );
  }

  if (typography) {
    return (
      <Typography
        variant={typographyVariant || 'body2'}
        display="inline"
        component="a"
        color="primary"
        target="_blank"
        style={{ textDecoration: 'none' }}
        href={
          useIndexLink && company.origin && company.slug
            ? keepitalPath('company.index', {
                origin: company.origin.toLowerCase(),
                companySlug: company.slug,
              })
            : adminPath('business.dashboard', {
                companyID: company._id as string,
              })
        }
      >
        {text}
        {verificationComponent}
        {useCopyButton && copyButton}
      </Typography>
    );
  }

  return (
    <span className={classes.textOnlyRoot}>
      {text}
      {verificationComponent}
      {externalLinkComponent}
    </span>
  );
}
